import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FileText,
  Plus,
  Edit,
  Trash2,
  Eye,
  History,
  Filter,
  Search,
  CheckCircle,
  XCircle,
  Clock,
  AlertCircle,
  Hash
} from 'lucide-react';
import { getAllTemplates, deleteTemplate } from '../../../services/documentTemplateService';
import logger from '../../../utils/logger';

const STATUS_CONFIG = {
  draft: {
    label: 'Borrador',
    icon: Clock,
    bgColor: 'bg-gray-100',
    textColor: 'text-gray-700',
    badgeColor: 'bg-gray-500'
  },
  active: {
    label: 'Activo',
    icon: CheckCircle,
    bgColor: 'bg-green-100',
    textColor: 'text-green-700',
    badgeColor: 'bg-green-500'
  },
  obsolete: {
    label: 'Obsoleto',
    icon: XCircle,
    bgColor: 'bg-red-100',
    textColor: 'text-red-700',
    badgeColor: 'bg-red-500'
  }
};

const DocumentTemplateList = ({ onEdit, onViewHistory, onCreate }) => {
  const [templates, setTemplates] = useState([]);
  const [filteredTemplates, setFilteredTemplates] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [filters, setFilters] = useState({
    status: 'all',
    process: 'all',
    search: ''
  });

  useEffect(() => {
    fetchTemplates();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [filters, templates]);

  const fetchTemplates = async () => {
    setIsLoading(true);
    setError('');
    try {
      const data = await getAllTemplates();
      setTemplates(data);
    } catch (err) {
      logger.error('Error loading templates:', err);
      setError(err.response?.data?.message || err.message || 'Error al cargar las plantillas');
    } finally {
      setIsLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...templates];

    // Filtro por estado
    if (filters.status !== 'all') {
      filtered = filtered.filter(t => t.status === filters.status);
    }

    // Filtro por proceso
    if (filters.process !== 'all') {
      filtered = filtered.filter(t => t.process === filters.process);
    }

    // Filtro por búsqueda
    if (filters.search.trim()) {
      const searchLower = filters.search.toLowerCase();
      filtered = filtered.filter(t =>
        t.name.toLowerCase().includes(searchLower) ||
        t.code.toLowerCase().includes(searchLower) ||
        (t.description && t.description.toLowerCase().includes(searchLower))
      );
    }

    setFilteredTemplates(filtered);
  };

  const handleDelete = async (template) => {
    if (!window.confirm(`¿Estás seguro de eliminar la plantilla "${template.name}"?\n\nEsta acción marcará la plantilla como obsoleta.`)) {
      return;
    }

    try {
      await deleteTemplate(template.id);
      fetchTemplates();
    } catch (err) {
      logger.error('Error deleting template:', err);
      alert(err.response?.data?.message || 'Error al eliminar la plantilla');
    }
  };

  const processes = [...new Set(templates.map(t => t.process).filter(Boolean))];

  if (isLoading) {
    return (
      <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100 flex items-center justify-center">
        <p className="text-gray-500">Cargando plantillas...</p>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className="bg-white rounded-2xl shadow-lg border border-blue-200 ring-2 ring-blue-200"
    >
      {/* Header */}
      <div className="p-6 border-b border-gray-200">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
              <FileText className="w-7 h-7 text-blue-600" />
              Plantillas de Documentos
            </h3>
            <p className="text-sm text-gray-500 mt-1">
              Gestión de plantillas según ISO 9001:2015 y NTC 185
            </p>
          </div>
          <motion.button
            onClick={onCreate}
            className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-blue-500 to-purple-600 text-white font-medium rounded-lg shadow-lg hover:shadow-xl transition-all duration-200"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <Plus className="w-5 h-5" />
            Nueva Plantilla
          </motion.button>
        </div>

        {/* Filtros */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {/* Búsqueda */}
          <div className="relative">
            <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
            <input
              type="text"
              placeholder="Buscar por nombre o código..."
              value={filters.search}
              onChange={(e) => setFilters({ ...filters, search: e.target.value })}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
            />
          </div>

          {/* Filtro por estado */}
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-gray-400" />
            <select
              value={filters.status}
              onChange={(e) => setFilters({ ...filters, status: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
            >
              <option value="all">Todos los estados</option>
              <option value="draft">Borrador</option>
              <option value="active">Activo</option>
              <option value="obsolete">Obsoleto</option>
            </select>
          </div>

          {/* Filtro por proceso */}
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-gray-400" />
            <select
              value={filters.process}
              onChange={(e) => setFilters({ ...filters, process: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
            >
              <option value="all">Todos los procesos</option>
              {processes.map(process => (
                <option key={process} value={process}>{process}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Error message */}
      {error && (
        <div className="p-4 mx-6 mt-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700">
          <AlertCircle className="w-5 h-5" />
          <span>{error}</span>
        </div>
      )}

      {/* Lista de plantillas */}
      <div className="p-6">
        {filteredTemplates.length === 0 ? (
          <div className="text-center py-12">
            <FileText className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500 text-lg">No se encontraron plantillas</p>
            <p className="text-gray-400 mt-2 text-sm">
              {filters.search || filters.status !== 'all' || filters.process !== 'all'
                ? 'Ajusta los filtros o crea una nueva plantilla'
                : 'Crea tu primera plantilla de documento'}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <AnimatePresence mode="popLayout">
              {filteredTemplates.map((template) => {
                const statusConfig = STATUS_CONFIG[template.status] || STATUS_CONFIG.draft;
                const StatusIcon = statusConfig.icon;

                return (
                  <motion.div
                    key={template.id}
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.9 }}
                    transition={{ duration: 0.2 }}
                    className="bg-gray-50 border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
                  >
                    {/* Header */}
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-xs font-mono text-gray-500 bg-gray-200 px-2 py-0.5 rounded">
                            {template.code}
                          </span>
                          <span className="text-xs text-gray-500">v{template.version}</span>
                        </div>
                        <h4 className="font-semibold text-gray-900 text-lg">{template.name}</h4>
                        {template.description && (
                          <p className="text-sm text-gray-600 mt-1 line-clamp-2">{template.description}</p>
                        )}
                      </div>
                      <div className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${statusConfig.bgColor} ${statusConfig.textColor}`}>
                        <StatusIcon className="w-3 h-3" />
                        {statusConfig.label}
                      </div>
                    </div>

                    {/* Info */}
                    <div className="grid grid-cols-2 gap-2 mb-3 text-xs">
                      <div>
                        <span className="text-gray-500">Proceso:</span>
                        <p className="font-medium text-gray-700">{template.process || 'N/A'}</p>
                      </div>
                      <div>
                        <span className="text-gray-500">Tipo:</span>
                        <p className="font-medium text-gray-700 capitalize">
                          {template.document_type || template.type || 'formato'}
                        </p>
                      </div>
                      <div>
                        <span className="text-gray-500 flex items-center gap-1">
                          <Hash className="w-3 h-3" />
                          Consecutivo:
                        </span>
                        <p className="font-medium text-gray-700">
                          {template.number_format || template.numberFormat
                            ? (template.number_format || template.numberFormat).replace(
                                /#+/g,
                                (template.next_number || template.nextNumber || 1).toString().padStart(4, '0')
                              )
                            : 'N/A'}
                        </p>
                      </div>
                      <div>
                        <span className="text-gray-500">Vigencia:</span>
                        <p className="font-medium text-gray-700">
                          {(template.effective_date || template.effectiveDate)
                            ? new Date(template.effective_date || template.effectiveDate).toLocaleDateString('es-CO')
                            : 'N/A'}
                        </p>
                      </div>
                    </div>

                    {/* Control SGC */}
                    <div className="border-t border-gray-300 pt-2 mb-3">
                      <p className="text-xs text-gray-600">
                        <span className="font-medium">Elaboró:</span>{' '}
                        {template.elaborated_by || template.elaboratedBy || 'N/A'}
                      </p>
                      <p className="text-xs text-gray-600">
                        <span className="font-medium">Revisó:</span>{' '}
                        {template.reviewed_by || template.reviewedBy || 'N/A'}
                      </p>
                      <p className="text-xs text-gray-600">
                        <span className="font-medium">Aprobó:</span>{' '}
                        {template.approved_by || template.approvedBy || 'N/A'}
                      </p>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-2">
                      <motion.button
                        onClick={() => onEdit(template)}
                        className="flex-1 flex items-center justify-center gap-1 px-3 py-2 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors text-sm font-medium"
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                      >
                        <Edit className="w-4 h-4" />
                        Editar
                      </motion.button>
                      <motion.button
                        onClick={() => onViewHistory(template)}
                        className="flex-1 flex items-center justify-center gap-1 px-3 py-2 bg-purple-100 text-purple-700 rounded-lg hover:bg-purple-200 transition-colors text-sm font-medium"
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                      >
                        <History className="w-4 h-4" />
                        Historial
                      </motion.button>
                      {template.status !== 'obsolete' && (
                        <motion.button
                          onClick={() => handleDelete(template)}
                          className="px-3 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition-colors"
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.98 }}
                        >
                          <Trash2 className="w-4 h-4" />
                        </motion.button>
                      )}
                    </div>
                  </motion.div>
                );
              })}
            </AnimatePresence>
          </div>
        )}
      </div>

      {/* Footer con estadísticas */}
      <div className="p-4 border-t border-gray-200 bg-gray-50 rounded-b-2xl">
        <div className="flex items-center justify-between text-sm">
          <p className="text-gray-600">
            Mostrando <span className="font-semibold">{filteredTemplates.length}</span> de{' '}
            <span className="font-semibold">{templates.length}</span> plantillas
          </p>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-1">
              <div className="w-2 h-2 bg-green-500 rounded-full"></div>
              <span className="text-gray-600">
                {templates.filter(t => t.status === 'active').length} Activas
              </span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-2 h-2 bg-gray-500 rounded-full"></div>
              <span className="text-gray-600">
                {templates.filter(t => t.status === 'draft').length} Borradores
              </span>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default DocumentTemplateList;
